import React, {useRef, useState} from "react"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import BankRiskSettingsEditMode from "./BankRiskSettingsEditMode"
import BankRiskSettingsViewMode from "./BankRiskSettingsViewMode"
import {useAccessToken} from "../../../services/auth"
import {useModal} from "react-modal-hook"
import {useRefreshToken} from "../../../hooks/useRefreshToken"
import {get, update} from "../../../resources/bankAgreementDefaults"
import {
	convertToBankAgreementDefaultsRiskSettings,
	convertToRiskSettings,
	RiskSettingsType,
} from "../../../components/RiskSettings/RiskSettingsUtils"
import SaveRiskSettingsConfirmationModal from "../../../components/RiskSettings/Modals/SaveRiskSettingsConfirmationModal"
import useAsyncResultIdle from "../../../hooks/useAsyncResultIdle"
import {FormikProps} from "formik"
import CancelRiskSettingsConfirmationModal from "../../../components/RiskSettings/Modals/CancelRiskSettingsConfirmationModal"
import {hasPermission} from "../../../services/permission"

interface RiskSettingsProps {
	bankId?: string
	title?: string
}

export default function RiskSettings({bankId, title}: RiskSettingsProps) {
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const bankAgreementDefaults = useAsyncResult(() => get(accessToken, bankId), [refreshToken])
	return (
		<AsyncResultComponent asyncResult={bankAgreementDefaults} pendingComponent={<></>}>
			{({value: bankAgreementDefaults}) => {
				const riskSettings = convertToRiskSettings(bankAgreementDefaults.attributes.riskSettings)
				const formRef = useRef<FormikProps<RiskSettingsType>>()
				const [editMode, setEditMode] = useState<boolean>(false)
				const setEditModeToFalse = () => setEditMode(false)
				const [showCancelConfirmationModal, hideCancelConfirmationModal] = useModal(
					() => (
						<CancelRiskSettingsConfirmationModal close={hideCancelConfirmationModal} onSubmit={setEditModeToFalse} />
					),
					[]
				)
				const [showSaveConfirmationModal, hideSaveConfirmationModal] = useModal(() => {
					const [state, callback] = useAsyncResultIdle(update)
					const onSubmit = async () => {
						if (formRef.current) {
							const formValues = formRef.current.values
							const patch = convertToBankAgreementDefaultsRiskSettings(formValues)
							await callback(accessToken, patch, bankAgreementDefaults.id)
						}
					}
					return (
						<SaveRiskSettingsConfirmationModal
							state={state}
							onSubmit={onSubmit}
							close={hideSaveConfirmationModal}
							refresh={refresh}
						/>
					)
				}, [])
				return editMode ? (
					<BankRiskSettingsEditMode
						initialValues={riskSettings}
						onCancel={showCancelConfirmationModal}
						onSave={showSaveConfirmationModal}
						formRef={formRef}
						title={title}
					/>
				) : (
					<BankRiskSettingsViewMode
						values={riskSettings}
						setEditMode={setEditMode}
						canEdit={hasPermission("bankAgreementDefaults", "update")}
						title={title}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
